import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Check, ChevronDown } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { PLANS } from "@/lib/planLimits";

const plans = PLANS.map((p) => ({
  name: p.name,
  desc: p.desc,
  price: p.price,
  features: [...p.features],
  popular: p.popular,
  key: p.key,
}));

const PricingSection = () => {
  const navigate = useNavigate();
  const [expandedPlan, setExpandedPlan] = useState<string | null>(null);

  return (
    <section id="pricing" className="py-24">
      <div className="mx-auto max-w-7xl px-6">
        <p className="text-sm font-semibold tracking-widest text-primary">Pricing</p>
        <h2 className="mt-2 text-3xl font-bold md:text-5xl">SIMPLE, TRANSPARENT PRICING</h2>
        <p className="mt-3 text-muted-foreground">Monthly billing in USD. Cancel anytime. All features included.</p>
        <div className="mt-12 grid gap-6 md:grid-cols-3">
          {plans.map((plan, i) => {
            const isExpanded = expandedPlan === plan.key;
            return (
              <div key={plan.name} className={`relative rounded-xl border p-8 animate-fade-in ${plan.popular ? "border-primary bg-card shadow-[0_0_30px_-10px_hsl(42_100%_50%/0.2)]" : "border-border bg-card"}`} style={{ animationDelay: `${i * 0.15}s`, animationFillMode: "backwards" }}>
                {plan.popular && <Badge className="absolute -top-3 left-8 rounded-full">Most Popular</Badge>}
                <h3 className="text-lg font-bold tracking-wider">{plan.name}</h3>
                <p className="mt-1 text-sm text-muted-foreground">{plan.desc}</p>
                <p className="mt-6 text-4xl font-bold">{plan.price}<span className="text-base font-normal text-muted-foreground">/month</span></p>

                {/* Toggle features button */}
                <button
                  type="button"
                  onClick={() => setExpandedPlan(isExpanded ? null : plan.key)}
                  className="mt-5 flex items-center gap-2 text-sm font-medium text-primary hover:text-primary/80 transition-colors w-full"
                >
                  <span>{isExpanded ? "Hide features" : "View all features"}</span>
                  <ChevronDown className={`h-4 w-4 transition-transform duration-300 ${isExpanded ? "rotate-180" : ""}`} />
                </button>

                {/* Collapsible features list */}
                <div className={`overflow-hidden transition-all duration-300 ease-in-out ${isExpanded ? "max-h-[500px] opacity-100 mt-4" : "max-h-0 opacity-0"}`}>
                  <ul className="space-y-3">
                    {plan.features.map((f) => (
                      <li key={f} className="flex items-center gap-2 text-sm text-muted-foreground"><Check className="h-4 w-4 text-primary flex-shrink-0" /> {f}</li>
                    ))}
                  </ul>
                </div>

                <Button className={`mt-6 w-full rounded-full ${plan.popular ? "" : "bg-secondary text-secondary-foreground hover:bg-secondary/80"}`} onClick={() => navigate(`/app/signup?plan=${encodeURIComponent(plan.key)}`)}>
                  Get Started
                </Button>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default PricingSection;
